import React, { useEffect, useRef } from 'react';
import { isPrerender } from '../utils/isPrerender';

interface FloatingElement {
  id: number;
  x: number;
  y: number;
  speedX: number;
  speedY: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  type: 'plus' | 'minus';
  opacity: number;
}

interface MousePosition {
  x: number;
  y: number;
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const elementsRef = useRef<FloatingElement[]>([]);
  const mouseRef = useRef<MousePosition>({ x: 0, y: 0 });

  useEffect(() => {
    // Отключаем анимацию для Prerender
    if (isPrerender()) {
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Устанавливаем размер canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Обработчик движения мыши
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Создаем плавающие элементы
    const createElements = () => {
      const elements: FloatingElement[] = [];
      const elementCount = Math.min(Math.floor(window.innerWidth / 120), 12); // Уменьшил количество элементов

      for (let i = 0; i < elementCount; i++) {
        elements.push({
          id: i,
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          speedX: (Math.random() - 0.5) * 0.3, // Увеличил скорость для более активного движения
          speedY: (Math.random() - 0.5) * 0.3,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 0.8, // Увеличил скорость вращения
          scale: 0.5 + Math.random() * 0.6, // Увеличил размер
          type: Math.random() > 0.5 ? 'plus' : 'minus',
          opacity: 0.12 + Math.random() * 0.2 // Увеличил прозрачность для контрастности
        });
      }
      return elements;
    };

    elementsRef.current = createElements();

    // Функция отрисовки элемента
    const drawElement = (element: FloatingElement) => {
      ctx.save();
      ctx.translate(element.x, element.y);
      ctx.rotate((element.rotation * Math.PI) / 180);
      ctx.scale(element.scale, element.scale);
      ctx.globalAlpha = element.opacity;

      if (element.type === 'plus') {
        // Рисуем плюс - более контрастный
        ctx.fillStyle = 'rgba(34, 197, 94, 0.5)'; // Зеленый - более контрастный
        ctx.strokeStyle = 'rgba(34, 197, 94, 0.8)'; // Более яркая обводка
        ctx.lineWidth = 2.5; // Толще линия
        
        // Вертикальная линия
        ctx.beginPath();
        ctx.moveTo(0, -15);
        ctx.lineTo(0, 15);
        ctx.stroke();
        
        // Горизонтальная линия
        ctx.beginPath();
        ctx.moveTo(-15, 0);
        ctx.lineTo(15, 0);
        ctx.stroke();
        
        // Круг вокруг
        ctx.beginPath();
        ctx.arc(0, 0, 20, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        // Рисуем минус - более контрастный
        ctx.fillStyle = 'rgba(239, 68, 68, 0.5)'; // Красный - более контрастный
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)'; // Более яркая обводка
        ctx.lineWidth = 2.5; // Толще линия
        
        // Горизонтальная линия
        ctx.beginPath();
        ctx.moveTo(-15, 0);
        ctx.lineTo(15, 0);
        ctx.stroke();
        
        // Круг вокруг
        ctx.beginPath();
        ctx.arc(0, 0, 20, 0, 2 * Math.PI);
        ctx.stroke();
      }

      ctx.restore();
    };

    // Функция анимации
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Обновляем и отрисовываем элементы
      elementsRef.current.forEach((element) => {
              // Взаимодействие с мышью
      const dx = mouseRef.current.x - element.x;
      const dy = mouseRef.current.y - element.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 150 && distance > 0) {
        const force = (150 - distance) / 150;
        if (element.type === 'plus') {
          // Плюсы притягиваются к мыши
          element.speedX += (dx / distance) * force * 0.15;
          element.speedY += (dy / distance) * force * 0.15;
        } else {
          // Минусы отталкиваются от мыши
          element.speedX -= (dx / distance) * force * 0.15;
          element.speedY -= (dy / distance) * force * 0.15;
        }
              } else {
          // Естественное активное движение, когда мышь далеко
          // Добавляем более частые случайные изменения направления
          if (Math.random() < 0.04) { // 4% шанс изменения направления
            element.speedX += (Math.random() - 0.5) * 0.2;
            element.speedY += (Math.random() - 0.5) * 0.2;
          }
          
          // Периодические "толчки" для более активного движения
          if (Math.random() < 0.008) { // 0.8% шанс сильного толчка
            element.speedX += (Math.random() - 0.5) * 0.4;
            element.speedY += (Math.random() - 0.5) * 0.4;
          }
        }

        // Обновляем позицию
        element.x += element.speedX;
        element.y += element.speedY;
        element.rotation += element.rotationSpeed;

        // Затухание скорости - менее сильное для более активного движения
        element.speedX *= 0.995;
        element.speedY *= 0.995;

        // Отскок от краев - более активный
        if (element.x < 0 || element.x > canvas.width) {
          element.speedX *= -0.9; // Меньше потери энергии при отскоке
        }
        if (element.y < 0 || element.y > canvas.height) {
          element.speedY *= -0.9;
        }

        // Ограничиваем позицию
        element.x = Math.max(0, Math.min(canvas.width, element.x));
        element.y = Math.max(0, Math.min(canvas.height, element.y));

        drawElement(element);
      });

      // Рисуем соединительные линии между близкими элементами - более заметные
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.08)'; // Увеличил прозрачность
      ctx.lineWidth = 0.8; // Увеличил толщину

      for (let i = 0; i < elementsRef.current.length; i++) {
        for (let j = i + 1; j < elementsRef.current.length; j++) {
          const elem1 = elementsRef.current[i];
          const elem2 = elementsRef.current[j];
          const distance = Math.sqrt(
            Math.pow(elem1.x - elem2.x, 2) + Math.pow(elem1.y - elem2.y, 2)
          );

          if (distance < 150) {
            ctx.globalAlpha = 1 - distance / 150;
            ctx.beginPath();
            ctx.moveTo(elem1.x, elem1.y);
            ctx.lineTo(elem2.x, elem2.y);
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ background: 'transparent', zIndex: -1 }}
    />
  );
};

export default AnimatedBackground; 